import { endOfMonth, format, isValid, parseISO, startOfMonth } from 'date-fns';
import { LOGISTICS_STATUSES, PAYOUT_STATUSES, PRODUCTION_STATUSES } from './orderStages';

export const EMPTY_ORDER_SUMMARY = {
    total_sales: 0,
    total_products: 0,
    total_amount: 0,
    average_ticket: 0,
    top_products: [],
};

/** Mês civil atual: dia 01 → último dia do mês. */
export function defaultOrdersDateRange(referenceDate = new Date()) {
    return {
        from: format(startOfMonth(referenceDate), 'yyyy-MM-dd'),
        to: format(endOfMonth(referenceDate), 'yyyy-MM-dd'),
    };
}

function parseOrdersDate(value) {
    if (!value || typeof value !== 'string') {
        return '';
    }
    const trimmed = value.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        return '';
    }
    const parsed = parseISO(trimmed);
    return isValid(parsed) ? trimmed : '';
}

export function normalizeOrdersDateRange(from, to) {
    let start = parseOrdersDate(from);
    let end = parseOrdersDate(to);

    if (start && end && start > end) {
        [start, end] = [end, start];
    }

    return { from: start, to: end };
}

export function ordersDateRangeForApi(from, to) {
    const { from: start, to: end } = normalizeOrdersDateRange(from, to);
    if (!start && !end) {
        return { from: '', to: '', error: null };
    }
    if (!start || !end) {
        return { from: null, to: null, error: 'Informe a data inicial e a data final.' };
    }
    return { from: start, to: end, error: null };
}

export function buildOrdersListQuery({ page, limit, q, from, to, salesChannelId, customerUuid, productionStatus, logisticsStatus, payoutStatus, paymentStatus, completed }) {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (q) {
        params.set('q', q);
    }
    if (from) {
        params.set('from', from);
    }
    if (to) {
        params.set('to', to);
    }
    if (salesChannelId) {
        params.set('sales_channel_id', salesChannelId);
    }
    if (customerUuid) {
        params.set('customer_uuid', customerUuid);
    }
    if (productionStatus) {
        params.set('production_status', productionStatus);
    }
    if (logisticsStatus) {
        params.set('logistics_status', logisticsStatus);
    }
    if (payoutStatus) {
        params.set('payout_status', payoutStatus);
    }
    if (paymentStatus) {
        params.set('payment_status', paymentStatus);
    }
    if (completed) {
        params.set('completed', completed);
    }
    return params.toString();
}

export function buildOrdersSummaryQuery({ q, from, to, salesChannelId, customerUuid }) {
    const params = new URLSearchParams();
    if (q) {
        params.set('q', q);
    }
    if (from) {
        params.set('from', from);
    }
    if (to) {
        params.set('to', to);
    }
    if (salesChannelId) {
        params.set('sales_channel_id', salesChannelId);
    }
    if (customerUuid) {
        params.set('customer_uuid', customerUuid);
    }
    return params.toString();
}

const ORDER_LIST_PATH = '/admin/orders/list';
const PAYMENT_STATUSES = ['paid', 'pending'];

function allowedParam(value, allowed) {
    const text = String(value || '').trim();
    return allowed.includes(text) ? text : '';
}

/** Path da lista com os filtros já aplicados, para voltar depois da edição. */
export function buildOrdersListLocation({
    q,
    from,
    to,
    salesChannelId,
    salesChannelName,
    customerUuid,
    customerName,
    productionStatus,
    logisticsStatus,
    payoutStatus,
    paymentStatus,
} = {}) {
    const params = new URLSearchParams();
    const set = (key, value) => {
        const text = String(value ?? '').trim();
        if (text) {
            params.set(key, text);
        }
    };
    set('search', q);
    params.set('from', String(from ?? ''));
    params.set('to', String(to ?? ''));
    set('sales_channel_id', salesChannelId);
    set('sales_channel_name', salesChannelId ? salesChannelName : '');
    set('customer_uuid', customerUuid);
    set('customer_name', customerUuid ? customerName : '');
    set('production_status', allowedParam(productionStatus, PRODUCTION_STATUSES));
    set('logistics_status', allowedParam(logisticsStatus, LOGISTICS_STATUSES));
    set('payout_status', allowedParam(payoutStatus, PAYOUT_STATUSES));
    set('payment_status', allowedParam(paymentStatus, PAYMENT_STATUSES));
    return `${ORDER_LIST_PATH}?${params.toString()}`;
}

export function parseOrdersListSearch(search, referenceDate = new Date()) {
    const params = new URLSearchParams(search || '');
    const customerUuid = (params.get('customer_uuid') || '').trim();
    const hasDateParams = params.has('from') || params.has('to');
    let from = '';
    let to = '';
    if (hasDateParams) {
        const normalized = normalizeOrdersDateRange(params.get('from') || '', params.get('to') || '');
        if (normalized.from && normalized.to) {
            from = normalized.from;
            to = normalized.to;
        }
    } else if (!customerUuid) {
        const defaults = defaultOrdersDateRange(referenceDate);
        from = defaults.from;
        to = defaults.to;
    }

    const salesChannelId = (params.get('sales_channel_id') || '').trim();
    return {
        searchTerm: (params.get('search') || '').trim(),
        from,
        to,
        salesChannelId,
        salesChannelName: salesChannelId ? (params.get('sales_channel_name') || '').trim() : '',
        customerUuid,
        customerName: customerUuid ? (params.get('customer_name') || '').trim() : '',
        productionStatus: allowedParam(params.get('production_status'), PRODUCTION_STATUSES),
        logisticsStatus: allowedParam(params.get('logistics_status'), LOGISTICS_STATUSES),
        payoutStatus: allowedParam(params.get('payout_status'), PAYOUT_STATUSES),
        paymentStatus: allowedParam(params.get('payment_status'), PAYMENT_STATUSES),
    };
}

/** Aceita só a lista de pedidos, para não redirecionar para um endereço externo. */
export function ordersListReturnPath(value) {
    if (typeof value !== 'string') {
        return '';
    }
    const trimmed = value.trim();
    if (!trimmed.startsWith(ORDER_LIST_PATH)) {
        return '';
    }
    const queryIndex = trimmed.indexOf('?');
    const path = queryIndex === -1 ? trimmed : trimmed.slice(0, queryIndex);
    if (path !== ORDER_LIST_PATH || trimmed.includes('://') || trimmed.startsWith('//')) {
        return '';
    }
    return trimmed;
}

export function orderEditPath(orderId, returnTo) {
    const base = `/admin/orders/${orderId}/edit`;
    const safeReturn = ordersListReturnPath(returnTo);
    if (!safeReturn) {
        return base;
    }
    return `${base}?return_to=${encodeURIComponent(safeReturn)}`;
}

export function customerOrdersPath(customer) {
    const params = new URLSearchParams();
    if (customer?.uuid) {
        params.set('customer_uuid', customer.uuid);
    }
    if (customer?.name) {
        params.set('customer_name', customer.name);
    }
    const query = params.toString();
    return query ? `/admin/orders/list?${query}` : '/admin/orders/list';
}

export async function fetchAllOpenKanbanOrders(client) {
    const orders = [];
    let page = 1;
    let totalPages = 1;
    do {
        const query = buildOrdersListQuery({
            page,
            limit: 100,
            completed: 'open',
        });
        const response = await client.get(`orders?${query}`);
        orders.push(...(Array.isArray(response.data?.orders) ? response.data.orders : []));
        totalPages = Math.max(1, Number(response.data?.total_pages) || 1);
        page += 1;
    } while (page <= totalPages);
    return orders;
}

export function formatOrdersPeriodLabel(from, to) {
    if (!from || !to) {
        return '';
    }
    const [fromYear, fromMonth, fromDay] = from.split('-');
    const [toYear, toMonth, toDay] = to.split('-');
    return `${fromDay}/${fromMonth}/${fromYear} a ${toDay}/${toMonth}/${toYear}`;
}
